/**
 * Duro editor widgets — the canvas renderers for the themed primitives, supplied
 * to the editor via its injection seam (registerWidget). They render the SAME
 * presentational Duro components the publish uses (primitives.tsx), so the canvas
 * is WYSIWYG with the published output.
 *
 * Editing: heading + card are inline-editable ON the canvas (type directly into
 * the real component). The `preview` flag (contentProps.preview) — true only in
 * an actual read-only preview — swaps back to the pure display primitive.
 *
 * Theming: the canvas applies the Brand scope (brandToDuroVars) around the
 * widgets, so these inherit the user's Brand the same way the published page does.
 */
import { useRef } from "react";
import type { WidgetProps } from "@website-builder/editor";
import { useUpdateGrid } from "@fredericrous/grid";
import { useDesignSystem } from "./designSystemContext.js";
import {
  PrimitiveButton,
  PrimitiveHeading,
  PrimitiveCard,
  PrimitiveInput,
  type InputContent,
  alignOf,
  type ButtonContent,
  type HeadingContent,
  type CardContent,
} from "./primitives.js";

const CARD_VARIANTS = [
  "elevated",
  "outlined",
  "filled",
  "interactive",
] as const;
function cardVariant(v: unknown): (typeof CARD_VARIANTS)[number] {
  return typeof v === "string" &&
    (CARD_VARIANTS as readonly string[]).includes(v)
    ? (v as (typeof CARD_VARIANTS)[number])
    : "outlined";
}
function headingLevel(level: unknown): 1 | 2 | 3 | 4 | 5 | 6 {
  return (typeof level === "number" && level >= 1 && level <= 6 ? level : 2) as
    | 1
    | 2
    | 3
    | 4
    | 5
    | 6;
}

/**
 * Inline-editable text: a contentEditable that commits to the grid store on
 * blur. UNCONTROLLED while typing (children are set once at mount and never
 * updated from props mid-edit) so React never rewrites the node and the caret
 * never jumps; the commit-on-blur re-render then matches the DOM. Carries the
 * same canvas-gesture opt-outs the text editor needs (nodrag + stopPropagation
 * + re-enabled text selection) so a click places the caret instead of grabbing
 * the block.
 */
function InlineEditable({
  value,
  placeholder,
  multiline,
  onCommit,
}: {
  value: string;
  placeholder: string;
  multiline?: boolean;
  onCommit: (text: string) => void;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  return (
    // eslint-disable-next-line duro/no-raw-html-element -- react-strict-dom's html.span does not forward contentEditable
    <span
      ref={ref}
      className="nodrag"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-label={placeholder}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (!multiline && e.key === "Enter") {
          e.preventDefault();
          ref.current?.blur();
        }
      }}
      onBlur={() => {
        const t = ref.current?.innerText ?? "";
        if (t !== value) onCommit(t);
      }}
      style={{
        display: "inline-block",
        minWidth: "1ch",
        outline: "none",
        cursor: "text",
        userSelect: "text",
        WebkitUserSelect: "text",
        touchAction: "auto",
        whiteSpace: multiline ? "pre-wrap" : "normal",
      }}
    >
      {value}
    </span>
  );
}

export function DuroButtonWidget({ contentProps }: WidgetProps) {
  // Button label editing is a follow-up (it's a control, not body copy).
  return <PrimitiveButton {...((contentProps ?? {}) as ButtonContent)} />;
}

/** The same Duro field the published page shows. Uncontrolled and outside
 *  any <form> on the canvas: typing never reaches the document, and nothing
 *  submits while editing (the published section form does that). */
export function DuroInputWidget({ contentProps }: WidgetProps) {
  return <PrimitiveInput {...((contentProps ?? {}) as InputContent)} />;
}

export function DuroHeadingWidget({
  itemId,
  gridId,
  contentProps,
}: WidgetProps) {
  const preview = contentProps?.["preview"] as boolean | undefined;
  const c = (contentProps ?? {}) as HeadingContent;
  const { updateItem } = useUpdateGrid(gridId);
  const { Heading } = useDesignSystem().components;
  if (preview) return <PrimitiveHeading {...c} />;
  const align = alignOf(c.align);
  return (
    <Heading level={headingLevel(c.level)} {...(align ? { align } : {})}>
      <InlineEditable
        value={c.text ?? ""}
        placeholder="Heading"
        onCommit={(text) =>
          updateItem(itemId, { contentProps: { ...c, text } })
        }
      />
    </Heading>
  );
}

export function DuroCardWidget({ itemId, gridId, contentProps }: WidgetProps) {
  const preview = contentProps?.["preview"] as boolean | undefined;
  const c = (contentProps ?? {}) as CardContent;
  const { updateItem } = useUpdateGrid(gridId);
  const { Card, Text } = useDesignSystem().components;
  if (preview) return <PrimitiveCard {...c} />;
  const set = (patch: Partial<CardContent>) =>
    updateItem(itemId, { contentProps: { ...c, ...patch } });
  return (
    <Card
      variant={cardVariant(c.variant)}
      // Card renders `header` in its header slot as a node; the prop is
      // under-typed as string, so cast the editable element through.
      header={
        (
          <InlineEditable
            value={c.title ?? ""}
            placeholder="Card title"
            onCommit={(title) => set({ title })}
          />
        ) as unknown as string
      }
    >
      <Text as="p">
        <InlineEditable
          value={c.body ?? ""}
          placeholder="Card body"
          multiline
          onCommit={(body) => set({ body })}
        />
      </Text>
    </Card>
  );
}
